import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';
import { arrayRemove, arrayUnion, doc, increment, serverTimestamp, updateDoc, writeBatch } from 'firebase/firestore';
import { Colors, Radius, Shadow, Typography } from '../theme';
import type { PlanJoinRequest, RootStackParamList } from '../types';
import { firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import TonyteBackground from '../components/TonyteBackground';
import VibeVerifiedBadge from '../components/VibeVerifiedBadge';
import { useAdminAllPendingRequests, useStudiZonePendingRequests } from '../hooks/usePlanJoinRequest';
import { getUserDisplayName, getUserProfile, TONYTE_PROFILE_IMAGE, userProfileExists } from '../utils/userProfiles';
import { useI18n } from '../i18n';
import { assertSocialWriteAllowed } from '../utils/verificationGate';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

type Props = NativeStackScreenProps<RootStackParamList, 'PlanRequests' | 'StudiZoneProfileRequests' | 'StudiZoneProfileRequestsScreen'>;

const PlanRequestsScreen: React.FC<Props> = ({ route }) => {
  const { userId, planId, highlightRequestId } = route.params;
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = React.useMemo(() => createRequestAppearanceStyles(theme), [theme]);
  const isStudiZone = route.name === 'StudiZoneProfileRequests' || route.name === 'StudiZoneProfileRequestsScreen';
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const publicRequests = useAdminAllPendingRequests(isStudiZone ? null : userId);
  const studiZoneRequests = useStudiZonePendingRequests(isStudiZone ? userId : null);
  const { requests: allRequests, loading } = isStudiZone ? studiZoneRequests : publicRequests;

  // [PushTapDebug] step 10 — proves navigation actually reached this screen
  // and mounted it, vs. navigate() being invoked but rendering failing
  // silently later. Temporary, remove after the debug pass.
  useEffect(() => {
    if (__DEV__) console.log('[PushTapDebug] PlanRequests mounted', { routeName: route.name, params: route.params });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Arriving from a join-request notification for a specific plan — filter
  // to that plan's requests. No param (the normal "view all my plans'
  // requests" entry point, e.g. ProfileScreen.tsx) keeps today's behavior
  // exactly: requests === allRequests.
  const filteredRequests = planId ? allRequests.filter(r => r.planId === planId) : allRequests;
  // Arriving from a notification tap for one specific request — surface it
  // first so it's visible without scrolling, on top of the highlight style.
  const requests = highlightRequestId
    ? [...filteredRequests].sort((a, b) => (a.id === highlightRequestId ? -1 : b.id === highlightRequestId ? 1 : 0))
    : filteredRequests;
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleAccept = useCallback(async (req: PlanJoinRequest) => {
    if (processingId) return;
    setProcessingId(req.id);
    try {
      assertSocialWriteAllowed();
      const userSnap = await getUserProfile(req.fromUserId).catch(() => null);
      const displayName = userSnap ? getUserDisplayName(userSnap) : t('planRequests.traveler');
      const photoURL = userSnap?.profileImage || '';

      const batch = writeBatch(firestore);
      batch.update(doc(firestore, FIRESTORE_COLLECTIONS.planJoinRequests, req.id), {
        status: isStudiZone ? 'approved' : 'accepted',
        reviewedBy: userId,
        reviewedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      batch.update(doc(firestore, FIRESTORE_COLLECTIONS.plans, req.planId), {
        participantIds: arrayUnion(req.fromUserId),
        members: arrayUnion(req.fromUserId),
        approvedRequests: arrayUnion(req.fromUserId),
        attendees: arrayUnion({ id: req.fromUserId, name: displayName, photo: photoURL }),
        participants: increment(1),
        // Clears a prior admin removal so a deliberately re-accepted user
        // isn't still filtered out of membership by planMembership.ts (which
        // subtracts removedUserIds) or blocked from Plan re-request UI.
        removedUserIds: arrayRemove(req.fromUserId),
      });
      batch.set(
        doc(firestore, FIRESTORE_COLLECTIONS.plans, req.planId, 'participants', req.fromUserId),
        { userId: req.fromUserId, displayName, photoURL, joinedAt: serverTimestamp() },
      );
      batch.update(doc(firestore, FIRESTORE_COLLECTIONS.planChats, req.planId), {
        memberIds: arrayUnion(req.fromUserId),
        members: arrayUnion(req.fromUserId),
        // Clears a prior admin removal's ban so the messages create rule
        // (which checks bannedUserIds) doesn't keep silently rejecting sends
        // from a user who was just deliberately re-admitted.
        bannedUserIds: arrayRemove(req.fromUserId),
        ...(isStudiZone ? { layer: 'studizone', chatType: 'studizone_plan', isStudentOnly: true } : {}),
      });
      await batch.commit();
    } catch (error) {
      console.warn('[Tonyte][PlanRequests] accept failed', { requestId: req.id, planId: req.planId, error });
      const reason = error instanceof Error ? error.message : String(error);
      Alert.alert(t('common.error'), `${t('planRequests.acceptFailed')}\n\n${reason}`);
    } finally {
      setProcessingId(null);
    }
  }, [isStudiZone, userId, processingId, t]);

  const handleDecline = useCallback(async (req: PlanJoinRequest) => {
    if (processingId) return;
    setProcessingId(req.id);
    try {
      await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.planJoinRequests, req.id), {
        status: isStudiZone ? 'rejected' : 'declined',
        reviewedBy: userId,
        reviewedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    } catch {
      Alert.alert(t('common.error'), t('planRequests.declineFailed'));
    } finally {
      setProcessingId(null);
    }
  }, [isStudiZone, userId, processingId, t]);

  // Reuses the same PublicProfile screen/navigation every other part of the
  // app uses to view a user (see ChatScreen.tsx's openOtherProfile) — no
  // separate "requester profile" screen. Existence is checked first since a
  // deleted account still normalizes to a populated-looking empty profile
  // (see userProfiles.ts), which would otherwise silently open a blank page
  // instead of surfacing "profile unavailable".
  const handleViewProfile = useCallback(async (req: PlanJoinRequest) => {
    const exists = await userProfileExists(req.fromUserId);
    if (!exists) {
      Alert.alert(t('planRequests.profileUnavailable'), t('planRequests.profileUnavailableBody'));
      return;
    }
    navigation.navigate('PublicProfile', { userId: req.fromUserId });
  }, [navigation, t]);

  return (
    <TonyteBackground appearanceAware style={[styles.container, appearance.container]}>
      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <TouchableOpacity style={[styles.backButton, appearance.controlSurface]} onPress={() => navigation.goBack()} activeOpacity={0.78}>
          <Icon name="arrow-left" size={20} color={theme.colors.icon} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={[styles.headerKicker, appearance.accentText]}>{isStudiZone ? t('planRequests.studiZoneAdmin') : t('planRequests.planAdmin')}</Text>
          <Text style={[styles.headerTitle, appearance.textPrimary]}>{isStudiZone ? t('planRequests.studiZoneRequests') : t('planRequests.joinRequests')}</Text>
        </View>
        <View style={[styles.headerCount, appearance.accentSurface]}>
          <Text style={[styles.headerCountText, appearance.accentText]}>{loading ? '…' : requests.length}</Text>
        </View>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, { paddingBottom: insets.bottom + 40 }]}
        showsVerticalScrollIndicator={false}
      >
        {loading ? (
          <View style={styles.emptyWrap}>
            <ActivityIndicator color={Colors.turquoise} />
          </View>
        ) : requests.length === 0 ? (
          <View style={styles.emptyWrap}>
            <View style={[styles.emptyIcon, appearance.accentSurface]}>
              <Icon name="users" size={28} color={Colors.turquoise} />
            </View>
            <Text style={[styles.emptyTitle, appearance.textPrimary]}>{t('planRequests.empty')}</Text>
            <Text style={[styles.emptyText, appearance.textSecondary]}>{t('planRequests.emptySub')}</Text>
          </View>
        ) : (
          requests.map(req => (
            <RequestCard
              key={req.id}
              request={req}
              processing={processingId === req.id}
              highlighted={req.id === highlightRequestId}
              onAccept={() => handleAccept(req)}
              onDecline={() => handleDecline(req)}
              onViewProfile={() => handleViewProfile(req)}
            />
          ))
        )}
      </ScrollView>
    </TonyteBackground>
  );
};

const RequestCard: React.FC<{
  request: PlanJoinRequest;
  processing: boolean;
  highlighted?: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onViewProfile: () => void;
}> = ({ request, processing, highlighted, onAccept, onDecline, onViewProfile }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = React.useMemo(() => createRequestAppearanceStyles(theme), [theme]);
  const [profile, setProfile] = useState<{ name: string; photo: string; faceVerified: boolean; isStudentVerified: boolean } | null>(null);

  useEffect(() => {
    getUserProfile(request.fromUserId)
      .then(p => setProfile({ name: getUserDisplayName(p), photo: p.profileImage || '', faceVerified: p.faceVerified === true, isStudentVerified: p.isStudentVerified === true }))
      .catch(() => setProfile({ name: t('planRequests.traveler'), photo: '', faceVerified: false, isStudentVerified: false }));
  }, [request.fromUserId, t]);

  return (
    <View style={[styles.card, appearance.card, highlighted && styles.cardHighlighted, highlighted && appearance.cardHighlighted]}>
      <LinearGradient
        pointerEvents="none"
        colors={theme.isDark
          ? (highlighted ? ['rgba(38,208,206,0.16)', 'rgba(38,208,206,0.03)'] : ['rgba(38,208,206,0.08)', 'rgba(38,208,206,0.02)'])
          : (highlighted ? ['rgba(18,166,166,0.14)', 'rgba(255,250,242,0.10)'] : ['rgba(18,166,166,0.06)', 'rgba(255,250,242,0.02)'])}
        style={StyleSheet.absoluteFill}
      />
      <TouchableOpacity
        style={styles.cardTop}
        onPress={onViewProfile}
        activeOpacity={0.72}
        accessibilityRole="button"
        accessibilityLabel={`${t('planRequests.viewProfile')} ${profile?.name ?? ''}`.trim()}
      >
        <View style={styles.avatarOuterWrap}>
          <View style={styles.avatarWrap}>
            {profile?.photo ? (
              <Image source={{ uri: profile.photo }} style={styles.avatar} resizeMode="cover" />
            ) : (
              <Image source={TONYTE_PROFILE_IMAGE} style={styles.avatar} resizeMode="cover" />
            )}
          </View>
          <VibeVerifiedBadge verified={profile?.faceVerified === true} size={56} />
        </View>
        <View style={styles.cardInfo}>
          <View style={styles.cardInfoTopRow}>
            <View style={[styles.planBadge, appearance.accentSurface]}>
              <Text style={[styles.planBadgeText, appearance.accentText]} numberOfLines={1}>{request.planTitle || t('planRequests.communityPlan')}</Text>
            </View>
            {highlighted && (
              <View style={styles.newBadge}>
                <Text style={styles.newBadgeText}>{t('planRequests.new')}</Text>
              </View>
            )}
          </View>
          <View style={styles.requesterNameRow}>
            <Text style={[styles.requesterName, appearance.textPrimary]}>{profile?.name ?? '…'}</Text>
            {profile?.faceVerified && (
              <View style={styles.verifyBadge}>
                <Icon name="user-check" size={9} color={Colors.turquoise} />
              </View>
            )}
            {profile?.isStudentVerified && (
              <View style={styles.verifyBadge}>
                <Icon name="award" size={9} color="#a78bfa" />
              </View>
            )}
          </View>
          {request.message ? (
            <Text style={[styles.message, appearance.textSecondary]} numberOfLines={3}>{request.message}</Text>
          ) : (
            <Text style={[styles.noMessage, appearance.textMuted]}>{t('planRequests.noMessage')}</Text>
          )}
        </View>
        <Icon name="chevron-right" size={18} color={theme.isDark ? 'rgba(255,255,255,0.28)' : 'rgba(10,20,30,0.28)'} style={styles.viewProfileChevron} />
      </TouchableOpacity>

      <View style={styles.cardActions}>
        <TouchableOpacity
          style={[styles.btnDecline, processing && styles.btnDisabled]}
          onPress={onDecline}
          activeOpacity={0.82}
          disabled={processing}
        >
          <Icon name="x" size={14} color="#F87171" />
          <Text style={styles.btnDeclineText}>{t('planRequests.decline')}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnAccept, processing && styles.btnDisabled]}
          onPress={onAccept}
          activeOpacity={0.82}
          disabled={processing}
        >
          {processing ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Icon name="check" size={14} color="#fff" />
              <Text style={styles.btnAcceptText}>{t('planRequests.accept')}</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: 'transparent', overflow: 'visible' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 16,
    gap: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCenter: { flex: 1 },
  headerKicker: {
    color: Colors.turquoise,
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 2.2,
    textTransform: 'uppercase',
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.bold,
    marginTop: 2,
  },
  headerCount: {
    minWidth: 36,
    height: 36,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.3)',
    backgroundColor: 'rgba(38,208,206,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  headerCountText: {
    color: Colors.turquoise,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  scroll: { flex: 1, backgroundColor: 'transparent', overflow: 'visible' },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 4,
    gap: 12,
  },
  emptyWrap: {
    paddingTop: 60,
    alignItems: 'center',
    gap: 12,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 22,
    backgroundColor: 'rgba(38,208,206,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  emptyText: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
  card: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.15)',
    backgroundColor: 'rgba(16,23,34,0.72)',
    overflow: 'hidden',
    padding: 16,
    gap: 14,
    ...Shadow.card,
  },
  cardHighlighted: {
    borderColor: 'rgba(38,208,206,0.55)',
    borderWidth: 1.5,
  },
  cardTop: {
    flexDirection: 'row',
    gap: 14,
  },
  cardInfoTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  newBadge: {
    borderRadius: Radius.pill,
    backgroundColor: Colors.turquoise,
    paddingHorizontal: 7,
    paddingVertical: 3,
  },
  newBadgeText: {
    color: '#0A0E16',
    fontSize: 8,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.2,
  },
  avatarOuterWrap: { width: 56, height: 56, flexShrink: 0 },
  avatarWrap: {
    width: 56,
    height: 56,
    borderRadius: 18,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.2)',
  },
  avatar: {
    width: 56,
    height: 56,
  },
  cardInfo: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  planBadge: {
    alignSelf: 'flex-start',
    flexShrink: 1,
    borderRadius: Radius.pill,
    backgroundColor: 'rgba(38,208,206,0.12)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    maxWidth: '100%',
  },
  planBadgeText: {
    color: Colors.turquoise,
    fontSize: 9,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  requesterNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  requesterName: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  verifyBadge: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(38,208,206,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  viewProfileChevron: {
    alignSelf: 'center',
    marginLeft: 2,
  },
  message: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 18,
  },
  noMessage: {
    color: Colors.textMuted,
    fontSize: Typography.xs,
    fontStyle: 'italic',
  },
  cardActions: {
    flexDirection: 'row',
    gap: 10,
  },
  btnDecline: {
    flex: 1,
    height: 42,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.3)',
    backgroundColor: 'rgba(248,113,113,0.07)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  btnDeclineText: {
    color: '#F87171',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  btnAccept: {
    flex: 1,
    height: 42,
    borderRadius: 14,
    backgroundColor: Colors.turquoise,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    ...Shadow.card,
  },
  btnAcceptText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  btnDisabled: {
    opacity: 0.45,
  },
});

const createRequestAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  accentText: daylightStyle(theme, { color: theme.colors.accentSecondary }),
  controlSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.card,
  }),
  accentSurface: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.borderStrong,
  }),
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.cardBackground,
    borderColor: theme.colors.cardBorder,
    ...theme.shadows.card,
  }),
  cardHighlighted: daylightStyle(theme, { borderColor: theme.colors.accentSecondary }),
});

export default PlanRequestsScreen;
